import type {
    ComponentStageData,
    ComponentStageHeuristicDefinition,
    PluginContext,
    ReadOnlyExtensionAPI
} from '../../types'
import {SemanticTypes} from '../../preprocess/conversionDataBuilders'
import {getParent, isChildOfHeader, updateTargetLayout} from '../common'
import type {StructureEntryItem} from '../../conversionDal/types'
import {getSourceLayoutPointer, getTargetLayoutPointer} from '../../conversionDal/utils/pointer'
import {SkipPredicate} from '../../enums'

const moveToHiddenComponents = ({conversionDal, componentPointer, stageRuntimeStore}: ComponentStageData) => {
    const parentPointer = getParent(conversionDal, componentPointer)

    if (parentPointer) {
        conversionDal.modify(parentPointer, (currentValue: StructureEntryItem) => ({
            ...currentValue,
            components: currentValue.components?.filter(compId => compId !== componentPointer.id),
            hiddenComponents: (currentValue.hiddenComponents ?? []).concat([componentPointer.id])
        }))
    } else {
        stageRuntimeStore.registerSkipPredicate(SkipPredicate.SHOULD_HALT_ALGO, () => true)
    }
}

const hideComponent = (componentStageData: ComponentStageData) => {
    updateTargetLayout(componentStageData, {
        componentLayout: {
            hidden: true,
            type: 'ComponentLayout'
        },
        itemLayout: {},
        containerLayout: {},
        type: 'SingleLayoutData'
    })

    moveToHiddenComponents(componentStageData)
}

export const keepHiddenHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(getTargetLayoutPointer(componentPointer))?.componentLayout?.hidden,
        apply: moveToHiddenComponents,
        heuristicCode: 'KEEP HIDDEN'
    }
]

export const hideComponentsHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.VERTICAL_LINE,
        apply: hideComponent,
        heuristicCode: 'HIDE VERTICAL LINE'
    },
    {
        predicate: (
            {componentPointer, config}: ComponentStageData,
            _contextHelper: PluginContext,
            readOnlyExtensionApi: ReadOnlyExtensionAPI
        ) => {
            return readOnlyExtensionApi.isClonedComponent(componentPointer, config.rootPointer)
        },
        apply: () => {},
        heuristicCode: 'DO NOT HANDLE COMPONENT DUPLICATION IN HEADER'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(getSourceLayoutPointer(componentPointer))?.componentLayout?.hidden,
        apply: hideComponent,
        heuristicCode: 'HIDDEN CASCADE'
    },
    {
        predicate: (
            {conversionDal, componentPointer, config}: ComponentStageData,
            _contextHelper: PluginContext,
            readOnlyExtensionApi: ReadOnlyExtensionAPI
        ) => {
            return (
                isChildOfHeader(componentPointer, conversionDal) &&
                readOnlyExtensionApi.isDuplicatedComponent(componentPointer, config.rootPointer)
            )
        },
        apply: hideComponent,
        heuristicCode: 'HIDE HEADER DUPLICATED COMPONENTS'
    }
]
